import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@lib/api';

/**
 * Zoprey Asistan — isimli AI aksiyonlarının hook'ları.
 * Chatbot yok: her hook tek bir isimli aksiyon düğmesine karşılık gelir.
 */

export interface AiDurum {
  aktif: boolean;
  yapilandirildi: boolean;
  /** Kullanıcı platform süper-admini mi (SUPER_ADMIN_EMAILS allowlist'i)?
   *  "AI Maliyet" menüsü YALNIZ bu bayrak true iken gösterilir —
   *  wildcard izin (tenant sahibi) yeterli değildir. */
  superAdmin: boolean;
  /** Tenant'ın kendi kredi sayaçları — TL/maliyet/tavan içermez. */
  kredi: { toplam: number; kullanilan: number; kalan: number };
  devre: { seviye: 'YESIL' | 'SARI' | 'TURUNCU' | 'KIRMIZI'; renk: 'yesil' | 'sari' | 'kirmizi' } | null;
  mesaj: string | null;
}

export interface AiSablon {
  id: string;
  ad: string;
  tip: 'BELGE' | 'EPOSTA';
  icerik: string;
  degiskenler: string[];
  kaynakDosyaAdi: string | null;
  varsayilanMi: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AiMaliyetPanosu {
  donem: string;
  tavanTRY: number;
  toplamTRY: number;
  kalanTRY: number;
  yuzde: number;
  projeksiyonTRY: number;
  cagriSayisi: number;
  toplamKredi: number;
  toplamInputToken: number;
  toplamOutputToken: number;
  devreKesici: {
    seviye: 'YESIL' | 'SARI' | 'TURUNCU' | 'KIRMIZI';
    renk: 'yesil' | 'sari' | 'kirmizi';
    aylikToplamTRY: number;
    tavanTRY: number;
    yuzde: number;
    donem: string;
  };
  tenantKirilimi: Array<{
    tenantId: string;
    tenantAd: string;
    plan: string | null;
    cagri: number;
    kredi: number;
    tlMaliyet: number;
  }>;
  kullaniciKirilimi: Array<{
    userId: string | null;
    kullanici: string;
    email: string | null;
    tenantAd: string;
    cagri: number;
    kredi: number;
    tlMaliyet: number;
  }>;
  aksiyonKirilimi: Array<{
    aksiyon: string;
    etiket: string;
    cagri: number;
    kredi: number;
    tlMaliyet: number;
  }>;
  modelKirilimi: Array<{
    model: string;
    cagri: number;
    inputTokens: number;
    outputTokens: number;
    tlMaliyet: number;
  }>;
  gunlukTrend: Array<{ gun: string; tl: number }>;
}

const AI_KEY = 'ai';

export function useAiDurum() {
  return useQuery({
    queryKey: [AI_KEY, 'durum'],
    queryFn: () => api.get('ai/durum').json<{ data: AiDurum }>().then((r) => r.data),
    staleTime: 30_000,
    retry: 1,
  });
}

/** Kredi bakiyesi değiştiği için her aksiyon sonrası durumu tazeler. */
function useDurumTazele() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [AI_KEY, 'durum'] });
}

export function useAiEpostaTaslak() {
  const tazele = useDurumTazele();
  return useMutation({
    mutationFn: (input: { musteriId?: string; alici?: string }) =>
      api
        .post('ai/eposta-taslak', { json: input, timeout: 60_000 })
        .json<{ data: { konu: string; govde: string; musteriBulundu: boolean } }>()
        .then((r) => r.data),
    onSuccess: tazele,
  });
}

export function useAiMusteriOzet() {
  const tazele = useDurumTazele();
  return useMutation({
    mutationFn: (musteriId: string) =>
      api
        .post('ai/musteri-ozet', { json: { musteriId }, timeout: 60_000 })
        .json<{ data: { ozet: string } }>()
        .then((r) => r.data),
    onSuccess: tazele,
  });
}

export function useAiAylikRapor() {
  const tazele = useDurumTazele();
  return useMutation({
    mutationFn: (input?: { yil?: number; ay?: number }) =>
      api
        .post('ai/aylik-rapor', { json: input ?? {}, timeout: 90_000 })
        .json<{ data: { donem: string; rapor: string; veriler: Record<string, unknown> } }>()
        .then((r) => r.data),
    onSuccess: tazele,
  });
}

export type AylikRaporDisaAktarFormat = 'docx' | 'pdf' | 'pptx';

/**
 * Zaten üretilmiş aylık rapor metnini docx/pdf/pptx dosyasına dönüştürüp
 * indirir. Yeni bir AI çağrısı yapmaz, kredi harcamaz — bu yüzden durum
 * tazelemesi yok.
 */
export function useAiAylikRaporDisaAktar() {
  return useMutation({
    mutationFn: (input: {
      format: AylikRaporDisaAktarFormat;
      donem: string;
      rapor: string;
    }) =>
      api
        .post('ai/aylik-rapor/disa-aktar', { json: input, timeout: 60_000 })
        .blob(),
  });
}

// ── Şablonlar ──────────────────────────────────────────────────────────────

export function useAiSablonlar() {
  return useQuery({
    queryKey: [AI_KEY, 'sablonlar'],
    queryFn: () => api.get('ai/sablonlar').json<{ data: AiSablon[] }>().then((r) => r.data),
  });
}

export function useAiDokumanSablon() {
  const qc = useQueryClient();
  const tazele = useDurumTazele();
  return useMutation({
    mutationFn: (dosya: File) => {
      const formData = new FormData();
      formData.append('dosya', dosya);
      return api
        .post('ai/dokuman-sablon', { body: formData, timeout: 120_000 })
        .json<{ data: AiSablon }>()
        .then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [AI_KEY, 'sablonlar'] });
      tazele();
    },
  });
}

export function useAiSablonGuncelle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string; ad?: string; icerik?: string }) =>
      api
        .patch(`ai/sablonlar/${id}`, { json: input })
        .json<{ data: AiSablon }>()
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [AI_KEY, 'sablonlar'] }),
  });
}

export function useAiSablonSil() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`ai/sablonlar/${id}`).json(),
    onSuccess: () => qc.invalidateQueries({ queryKey: [AI_KEY, 'sablonlar'] }),
  });
}

// ── Süper-admin maliyet panosu ─────────────────────────────────────────────

export function useAiMaliyet(donem?: string) {
  return useQuery({
    queryKey: [AI_KEY, 'maliyet', donem ?? 'guncel'],
    queryFn: () =>
      api
        .get('ai/admin/maliyet', {
          searchParams: donem ? { donem } : undefined,
        })
        .json<{ data: AiMaliyetPanosu }>()
        .then((r) => r.data),
    retry: false,
  });
}
